import { useCallback, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import ImageCarousel from '@/components/ui/trainers/ImageCarousel';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/shadcn/dialog';
import { Button } from '@/components/shadcn/button';
import CloseIcon from '../icons/CloseIcon';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
  variant: 'correctness' | 'completeness';
}

const TrainingDataImage: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
  variant,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const { leaseId, image, annotations, labels } = data?.content || {};

  const description =
    initialDescription ||
    (data?.content
      ? `Identify the type of the structure for which the height restriction sign is installed.`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onSubmit = async ({ outcome }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }
    const sending = {
      leaseId,
      outcome: outcome ?? 'na',
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="relative flex size-full flex-col items-center justify-center">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <div className="relative flex size-full min-h-0 flex-1 flex-col items-center justify-center">
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button
                variant={'dark'}
                className="absolute right-4 top-4 z-10 bg-neutral-1000/90">
                <Info className="size-4" />
                <span className="ml-2">See all labels</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="data-[fullscreen=true]:h-dvh data-[fullscreen=true]:max-w-full data-[fullscreen=true]:p-0">
              <div
                onClick={() => {
                  setShowModal(false);
                }}
                className="absolute right-5 top-5 z-50 flex cursor-pointer items-center">
                <CloseIcon className="fill-neutral-0" />
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-4">
                <h3 className="mb-4 text-xl font-bold">Labels</h3>
                <div className="flex flex-col gap-1 overflow-y-auto">
                  {labels &&
                    labels.map((label, index) => (
                      <div key={index} className="rounded ">
                        {label
                          .split('-')
                          .map(
                            word =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase(),
                          )
                          .join(' ')}
                      </div>
                    ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="flex-1 flex-col items-center justify-center overflow-auto">
            {image && annotations && (
              <ImageCarousel
                images={[image]}
                annotations={annotations}
                annotationVariant={variant}
                startWithIndex="largestDetection"
              />
            )}
          </div>
          <div>
            <TrainerController
              containerRef={controllerRef}
              isDataReady={isDataReady}
              onSubmit={onSubmit}
              data={data}
              tool={tool}>
              <TrainerButtons />
              <TrainerSelect />
              <TrainerInputs />
            </TrainerController>
          </div>
        </div>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={checked => setIsBenchmark(checked)}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default TrainingDataImage;
